import React, { useState, useEffect, useMemo } from 'react'
import { InterviewPrepResponse, InterviewTypePrep, CompanySpecificPrep, PersonalizedRecommendations } from '../services/jobApplicationService'
import './InterviewPrepDisplay.css'
import { sendDebugLog } from '../utils/debugLogging'

// Debug logging helper - uses fetch on localhost, console.log in production (to avoid CORS)
const debugLog = (logData: any) => {
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  if (isLocalhost) {
    fetch('http://127.0.0.1:7243/ingest/a4c88e35-b16a-4489-824c-ed4c6e858c3f',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(logData)}).catch(()=>{});
  } else {
    console.log(`[DEBUG ${logData.hypothesisId || '?'}] ${logData.location}: ${logData.message}`, logData.data || {});
  }
};

interface InterviewPrepDisplayProps {
  interviewPrep: InterviewPrepResponse | null
  isLoading?: boolean
  error?: string | null
  companyName?: string | null
}

export const InterviewPrepDisplay: React.FC<InterviewPrepDisplayProps> = ({
  interviewPrep,
  isLoading = false,
  error = null,
  companyName = null
}) => {
  // #region agent log IMMEDIATE_RENDER_START
  // IMMEDIATE console.log - executes synchronously at render start
  // Wrap in try-catch to prevent errors from breaking the component
  try {
    console.log('[DEBUG RENDER START] InterviewPrepDisplay rendering', {
      hasPrep: !!interviewPrep,
      prepStatus: interviewPrep?.status,
      isLoading,
      hasError: !!error,
      prepType: typeof interviewPrep,
      prepKeys: interviewPrep ? Object.keys(interviewPrep) : [],
      hypothesisId: 'RENDER_START'
    });
  } catch (e) {
    console.error('[DEBUG ERROR] Failed to log render start', e);
  }
  // #endregion
  
  // CRITICAL: All hooks must be declared unconditionally at the top level
  // This ensures consistent hook order and prevents React error #310
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set())
  
  // #region agent log INTERVIEW_PREP_DISPLAY_RENDER
  useEffect(() => {
    sendDebugLog({
      sessionId: 'debug-session',
      runId: 'run7',
      hypothesisId: 'W',
      location: 'InterviewPrepDisplay.tsx:20',
      message: 'InterviewPrepDisplay rendering',
      data: {
        hasPrep: !!interviewPrep,
        prepStatus: interviewPrep?.status,
        isLoading,
        hasError: !!error,
        willReturnNull: !interviewPrep || interviewPrep.status !== 'ok',
      },
      timestamp: Date.now(),
    });
  }, [interviewPrep, isLoading, error]);
  // #endregion

  const toggleType = (type: string) => {
    const newExpanded = new Set(expandedTypes)
    if (newExpanded.has(type)) {
      newExpanded.delete(type)
    } else {
      newExpanded.add(type)
    }
    setExpandedTypes(newExpanded)
  }

  // Extract interview prep data early for use in useMemo
  let interview_types: InterviewPrepResponse['interview_types'], 
      company_specific: CompanySpecificPrep | undefined, 
      personalized_recommendations: PersonalizedRecommendations | undefined;
  
  if (interviewPrep && interviewPrep.status === 'ok') {
    ({ interview_types, company_specific, personalized_recommendations } = interviewPrep);
  }

  // Memoize filtered interview types to prevent hydration mismatches
  // NOTE: Only depend on interview_types, NOT expandedTypes (expanded state shouldn't affect filtering)
  const filteredInterviewTypes = useMemo(() => {
    console.log('[DEBUG USEMEMO] Calculating filteredInterviewTypes', {
      hasInterviewTypes: !!interview_types,
      interviewTypesType: typeof interview_types,
      interviewTypesKeys: interview_types ? Object.keys(interview_types) : [],
      hypothesisId: 'USEMEMO_CALC'
    });
    if (!interview_types || typeof interview_types !== 'object') {
      return []
    }
    const entries = Object.entries(interview_types)
    const filtered = entries.filter(([type, prep]: [string, InterviewTypePrep | undefined]) => {
      const isValid = prep != null && typeof prep === 'object'
      if (!isValid) {
        console.log('[DEBUG FILTER] Filtering out invalid prep', { type, prepType: typeof prep, hasPrep: !!prep })
      }
      return isValid
    })
    debugLog({
      location: 'InterviewPrepDisplay.tsx:filteredInterviewTypes',
      message: 'Filtering interview types',
      data: {
        totalEntries: entries.length,
        filteredCount: filtered.length,
        hasNullPreps: entries.some(([, prep]) => prep == null),
        entryTypes: entries.map(([type]) => type)
      },
      timestamp: Date.now(),
      sessionId: 'debug-session',
      runId: 'run1',
      hypothesisId: 'H4'
    })
    return filtered
  }, [interview_types])

  // CRITICAL: Early returns AFTER all hooks to ensure consistent hook order
  // This prevents React error #310 (hooks order violation)
  if (isLoading) {
    return (
      <div className="interview-prep">
        <div className="prep-header">
          <h3>🎯 Interview Preparation</h3>
        </div>
        <div className="prep-loading">
          <div className="spinner"></div>
          <p>Preparing interview guidance... This usually takes about 30-60 seconds.</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="interview-prep">
        <div className="prep-header">
          <h3>🎯 Interview Preparation</h3>
        </div>
        <div className="prep-error">
          <p>⚠️ {error}</p>
        </div>
      </div>
    )
  }

  // Validate interviewPrep structure before rendering
  console.log('[DEBUG VALIDATION] Before validation checks', {
    hasPrep: !!interviewPrep,
    prepType: typeof interviewPrep,
    prepStatus: interviewPrep?.status,
    hasCompany: !!interviewPrep?.company,
    hasPosition: !!interviewPrep?.position,
    hypothesisId: 'VALIDATION'
  });
  
  if (!interviewPrep || typeof interviewPrep !== 'object') {
    console.log('[DEBUG VALIDATION] Returning null - prep is not object', { prepType: typeof interviewPrep });
    return null
  }
  
  if (interviewPrep.status !== 'ok') {
    console.log('[DEBUG VALIDATION] Returning null - status is not ok', { status: interviewPrep.status });
    return null
  }
  
  // Validate required fields exist
  if (!interviewPrep.company || !interviewPrep.position) {
    console.warn('[DEBUG VALIDATION] Interview prep missing required fields (company or position)', {
      hasCompany: !!interviewPrep.company,
      hasPosition: !!interviewPrep.position
    });
    return null
  }

  // Additional validation: ensure data structure is complete
  if (interviewPrep.status === 'ok') {
    // For status 'ok', we expect complete data
    if (!interviewPrep.interview_types && !interviewPrep.company_specific && !interviewPrep.personalized_recommendations) {
      console.warn('[DEBUG VALIDATION] Interview prep has status "ok" but no actual data - treating as error', {
        hasInterviewTypes: !!interviewPrep.interview_types,
        hasCompanySpecific: !!interviewPrep.company_specific,
        hasPersonalized: !!interviewPrep.personalized_recommendations
      });
      return (
        <div className="interview-prep">
          <div className="prep-error">
            <p>⚠️ Interview preparation data is incomplete. Please try again.</p>
          </div>
        </div>
      )
    }
  }
  
  console.log('[DEBUG VALIDATION] Validation passed, proceeding to render', {
    hasInterviewTypes: !!interviewPrep.interview_types,
    hasCompanySpecific: !!interviewPrep.company_specific,
    hasPersonalized: !!interviewPrep.personalized_recommendations,
    hypothesisId: 'VALIDATION_PASSED'
  });

  // #region agent log INTERVIEW_PREP_DISPLAY_DATA_EXTRACTION
  try {
    sendDebugLog({
      sessionId: 'debug-session',
      runId: 'run6',
      hypothesisId: 'W',
      location: 'InterviewPrepDisplay.tsx:105',
      message: 'About to render interview prep data',
      data: {
        hasInterviewTypes: !!interview_types,
        interviewTypesKeys: interview_types ? Object.keys(interview_types) : [],
        hasCompanySpecific: !!company_specific,
        companySpecificKeys: company_specific ? Object.keys(company_specific) : [],
        hasPersonalized: !!personalized_recommendations,
        personalizedKeys: personalized_recommendations ? Object.keys(personalized_recommendations) : [],
      },
      timestamp: Date.now(),
    });
  } catch (extractError) {
    sendDebugLog({
      sessionId: 'debug-session',
      runId: 'run6',
      hypothesisId: 'W',
      location: 'InterviewPrepDisplay.tsx:125',
      message: 'Error extracting interview prep data',
      data: {
        errorMessage: extractError instanceof Error ? extractError.message : String(extractError),
        errorStack: extractError instanceof Error ? extractError.stack?.substring(0, 500) : undefined,
      },
      timestamp: Date.now(),
    });
    return null;
  }
  // #endregion

  const getInterviewTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      hr: 'HR Interview',
      professional_assessment: 'Professional Assessment',
      behavioral: 'Behavioral Interview',
      case_study: 'Case Study Interview',
      panel: 'Panel Interview'
    }
    return labels[type] || type
  }

  const getInterviewTypeIcon = (type: string): string => {
    const icons: Record<string, string> = {
      hr: '👥',
      professional_assessment: '💼',
      behavioral: '🤝',
      case_study: '📊',
      panel: '👨‍👩‍👧‍👦'
    }
    return icons[type] || '📝'
  }

  // #region agent log BEFORE_RENDER
  console.log('[DEBUG H4] Before render validation', {
    hasInterviewPrep: !!interviewPrep,
    prepStatus: interviewPrep?.status,
    hasCompanySpecific: !!company_specific,
    hasInterviewTypes: !!interview_types,
    hasPersonalized: !!personalized_recommendations,
    companySpecificKeys: company_specific ? Object.keys(company_specific) : [],
    hypothesisId: 'H4'
  });
  debugLog({location:'InterviewPrepDisplay.tsx:168',message:'Before render validation',data:{hasInterviewPrep:!!interviewPrep,prepStatus:interviewPrep?.status,hasCompanySpecific:!!company_specific,hasInterviewTypes:!!interview_types,hasPersonalized:!!personalized_recommendations,companySpecificKeys:company_specific?Object.keys(company_specific):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H4'});
  try {
    // Validate data structures before rendering
    if (company_specific && typeof company_specific.overview === 'string') {
      company_specific.overview.split('\n\n'); // Test split operation
    }
    if (company_specific?.values && !Array.isArray(company_specific.values)) {
      throw new Error('company_specific.values is not an array');
    }
    if (company_specific?.recent_news && !Array.isArray(company_specific.recent_news)) {
      throw new Error('company_specific.recent_news is not an array');
    }
    if (interview_types && typeof interview_types !== 'object') {
      throw new Error('interview_types is not an object');
    }
    if (personalized_recommendations && typeof personalized_recommendations !== 'object') {
      throw new Error('personalized_recommendations is not an object');
    }
    // #region agent log VALIDATION_PASSED
    debugLog({location:'InterviewPrepDisplay.tsx:185',message:'Validation passed, proceeding to render',data:{hasCompanySpecific:!!company_specific,hasInterviewTypes:!!interview_types,hasPersonalized:!!personalized_recommendations},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H4'});
    // #endregion
  } catch (validationError) {
    debugLog({location:'InterviewPrepDisplay.tsx:186',message:'Data validation error before render',data:{errorMessage:validationError instanceof Error ? validationError.message : String(validationError),errorStack:validationError instanceof Error ? validationError.stack?.substring(0, 500) : undefined},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H4'});
    return <div className="interview-prep-error">Error: Invalid data structure</div>;
  }
  // #endregion

  console.log('[DEBUG RENDER] About to return JSX', {
    hasCompanySpecific: !!company_specific,
    hasInterviewTypes: !!interview_types,
    hasPersonalized: !!personalized_recommendations,
    hypothesisId: 'RENDER_JSX'
  });
  
  return (
    <div className="interview-prep">
      {/* Company-Specific Information */}
      {company_specific && (
        <div className="prep-section company-specific-section">
          <h4>🏢 About {companyName || 'This Company'}</h4>
          {company_specific.overview && typeof company_specific.overview === 'string' && company_specific.overview.trim() && (
            <div className="info-card">
              <h5>Company Overview</h5>
              <div className="company-overview">
                {(() => {
                  if (typeof company_specific.overview !== 'string') return null;
                  const paragraphs = company_specific.overview
                    .split('\n\n')
                    .filter(p => p.trim()); // Filter out empty paragraphs FIRST
                  return paragraphs.map((paragraph, idx) => (
                    <p key={`overview-${idx}-${paragraph.substring(0, 20)}`}>{paragraph.trim()}</p>
                  ));
                })()}
              </div>
            </div>
          )}
          {company_specific.culture && (
            <div className="info-card">
              <h5>Company Culture</h5>
              <p>{company_specific.culture}</p>
            </div>
          )}
          {company_specific.values && Array.isArray(company_specific.values) && company_specific.values.length > 0 && (
            <div className="info-card">
              <h5>Company Values</h5>
              <ul>
                {(() => {
                  // #region agent log FILTERING_VALUES
                  const filtered = company_specific.values.filter((value: string | null | undefined) => value != null && String(value).trim());
                  debugLog({location:'InterviewPrepDisplay.tsx:235',message:'Filtering company values array',data:{originalLength:company_specific.values.length,filteredLength:filtered.length,hasNullValues:company_specific.values.some((v: string | null | undefined)=>v==null),hasEmptyValues:company_specific.values.some((v: string | null | undefined)=>!String(v).trim())},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H4'});
                  return filtered.map((value: string, idx: number) => (
                    <li key={`value-${idx}-${String(value).substring(0, 20)}`}>{String(value).trim()}</li>
                  ));
                })()}
              </ul>
            </div>
          )}
          {company_specific.recent_news && Array.isArray(company_specific.recent_news) && company_specific.recent_news.length > 0 && (
            <div className="info-card">
              <h5>Recent News</h5>
              <ul>
                {company_specific.recent_news
                  .filter((news: string | null | undefined) => news != null && String(news).trim())
                  .map((news: string, idx: number) => (
                    <li key={`news-${idx}-${String(news).substring(0, 30)}`}>{String(news).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
          {company_specific.interview_process && (
            <div className="info-card">
              <h5>Interview Process</h5>
              <p>{company_specific.interview_process}</p>
            </div>
          )}
          {company_specific.what_company_values && (
            <div className="info-card">
              <h5>What This Company Values in Candidates</h5>
              <p>{company_specific.what_company_values}</p>
            </div>
          )}
        </div>
      )}

      {/* Interview Types */}
      {interview_types && typeof interview_types === 'object' && (
        <div className="prep-section interview-types-section">
          <h4>📋 Interview Types</h4>
          <div className="interview-types-grid">
            {filteredInterviewTypes.map(([type, prep]: [string, InterviewTypePrep | undefined]) => {
              // Double-check (should never be true after filter, but defensive)
              if (!prep || typeof prep !== 'object') {
                console.error('[DEBUG ERROR] Invalid prep passed filter - this should not happen', { type, prepType: typeof prep, hasPrep: !!prep })
                // Return empty fragment instead of null to avoid hydration issues
                return <React.Fragment key={type}></React.Fragment>
              }
              const isExpanded = expandedTypes.has(type)
              const typedPrep: InterviewTypePrep = prep
            
              // #region agent log RENDERING_INTERVIEW_TYPE
              console.log('[DEBUG RENDER CARD] Rendering interview type card', {
                type,
                isExpanded,
                expandedTypesSize: expandedTypes.size,
                expandedTypesArray: Array.from(expandedTypes),
                prepKeys: Object.keys(typedPrep),
                hasPreparationGuide: !!typedPrep.preparation_guide,
                willRenderContent: isExpanded,
                hypothesisId: 'H5'
              });
              debugLog({location:'InterviewPrepDisplay.tsx:282',message:'Rendering interview type card',data:{type,isExpanded,expandedTypesSize:expandedTypes.size,expandedTypesArray:Array.from(expandedTypes),prepKeys:Object.keys(typedPrep),hasPreparationGuide:!!typedPrep.preparation_guide,willRenderContent:isExpanded},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H5'});
              // #endregion
              return (
                <div key={type} className="interview-type-card">
                  <div 
                    className="type-header" 
                    onClick={() => toggleType(type)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="type-title">
                      <span className="type-icon">{getInterviewTypeIcon(type)}</span>
                      <h5>{getInterviewTypeLabel(type)}</h5>
                    </div>
                    <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                  </div>
                  
                  {/* Always render the container to maintain stable structure */}
                  <div className="type-content" style={{ display: isExpanded ? 'block' : 'none' }}>
                      {typedPrep.preparation_guide && (
                        <div className="prep-item">
                          <strong>Preparation Guide:</strong>
                          <p>{typedPrep.preparation_guide}</p>
                        </div>
                      )}
                      
                      {typedPrep.common_questions && Array.isArray(typedPrep.common_questions) && typedPrep.common_questions.length > 0 && (
                        <div className="prep-item">
                          <strong>Common Questions:</strong>
                          <ul>
                            {typedPrep.common_questions
                              .filter((q: string | null | undefined) => q != null && String(q).trim())
                              .map((question: string, idx: number) => (
                                <li key={`question-${type}-${idx}-${String(question).substring(0, 30)}`}>{String(question).trim()}</li>
                              ))}
                          </ul>
                        </div>
                      )}
                      
                      {typedPrep.focus_areas && Array.isArray(typedPrep.focus_areas) && typedPrep.focus_areas.length > 0 && (
                        <div className="prep-item">
                          <strong>Focus Areas:</strong>
                          <ul>
                            {typedPrep.focus_areas
                              .filter((area: string | null | undefined) => area != null && String(area).trim())
                              .map((area: string, idx: number) => (
                                <li key={`area-${type}-${idx}-${String(area).substring(0, 30)}`}>{String(area).trim()}</li>
                              ))}
                          </ul>
                        </div>
                      )}
                      
                      {typedPrep.tips && Array.isArray(typedPrep.tips) && typedPrep.tips.length > 0 && (
                        <div className="prep-item">
                          <strong>Tips:</strong>
                          <ul>
                            {typedPrep.tips
                              .filter((tip: string | null | undefined) => tip != null && String(tip).trim())
                              .map((tip: string, idx: number) => (
                                <li key={`tip-${type}-${idx}-${String(tip).substring(0, 30)}`}>{String(tip).trim()}</li>
                              ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Professional Assessment specific fields */}
                      {type === 'professional_assessment' && (
                        <>
                          {typedPrep.technical_topics && Array.isArray(typedPrep.technical_topics) && typedPrep.technical_topics.length > 0 && (
                            <div className="prep-item">
                              <strong>Technical Topics to Review:</strong>
                              <ul>
                                {typedPrep.technical_topics
                                  .filter((topic: string | null | undefined) => topic != null && String(topic).trim())
                                  .map((topic: string, idx: number) => (
                                    <li key={`topic-${type}-${idx}-${String(topic).substring(0, 30)}`}>{String(topic).trim()}</li>
                                  ))}
                              </ul>
                            </div>
                          )}
                          
                          {typedPrep.expected_problems && Array.isArray(typedPrep.expected_problems) && typedPrep.expected_problems.length > 0 && (
                            <div className="prep-item">
                              <strong>Expected Problems/Scenarios:</strong>
                              <ul>
                                {typedPrep.expected_problems
                                  .filter((problem: string | null | undefined) => problem != null && String(problem).trim())
                                  .map((problem: string, idx: number) => (
                                    <li key={`problem-${type}-${idx}-${String(problem).substring(0, 30)}`}>{String(problem).trim()}</li>
                                  ))}
                              </ul>
                            </div>
                          )}
                          
                          {typedPrep.tools_frameworks && Array.isArray(typedPrep.tools_frameworks) && typedPrep.tools_frameworks.length > 0 && (
                            <div className="prep-item">
                              <strong>Tools & Frameworks to Know:</strong>
                              <ul>
                                {typedPrep.tools_frameworks
                                  .filter((tool: string | null | undefined) => tool != null && String(tool).trim())
                                  .map((tool: string, idx: number) => (
                                    <li key={`tool-${type}-${idx}-${String(tool).substring(0, 30)}`}>{String(tool).trim()}</li>
                                  ))}
                              </ul>
                            </div>
                          )}
                          
                          {typedPrep.study_resources && Array.isArray(typedPrep.study_resources) && typedPrep.study_resources.length > 0 && (
                            <div className="prep-item">
                              <strong>Study Resources:</strong>
                              <ul>
                                {typedPrep.study_resources
                                  .filter((resource: string | null | undefined) => resource != null && String(resource).trim())
                                  .map((resource: string, idx: number) => (
                                    <li key={`resource-${type}-${idx}-${String(resource).substring(0, 30)}`}>{String(resource).trim()}</li>
                                  ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Behavioral specific fields */}
                      {type === 'behavioral' && typedPrep.star_method_guide && (
                        <div className="prep-item">
                          <strong>STAR Method Guide:</strong>
                          <p>{typedPrep.star_method_guide}</p>
                        </div>
                      )}
                      
                      {/* Case Study specific fields */}
                      {type === 'case_study' && (
                        <>
                          {typedPrep.typical_scenarios && Array.isArray(typedPrep.typical_scenarios) && typedPrep.typical_scenarios.length > 0 && (
                            <div className="prep-item">
                              <strong>Typical Scenarios:</strong>
                              <ul>
                                {typedPrep.typical_scenarios
                                  .filter((scenario: string | null | undefined) => scenario != null && String(scenario).trim())
                                  .map((scenario: string, idx: number) => (
                                    <li key={`scenario-${type}-${idx}-${String(scenario).substring(0, 30)}`}>{String(scenario).trim()}</li>
                                  ))}
                              </ul>
                            </div>
                          )}
                          
                          {typedPrep.frameworks && Array.isArray(typedPrep.frameworks) && typedPrep.frameworks.length > 0 && (
                            <div className="prep-item">
                              <strong>Frameworks to Use:</strong>
                              <ul>
                                {typedPrep.frameworks
                                  .filter((framework: string | null | undefined) => framework != null && String(framework).trim())
                                  .map((framework: string, idx: number) => (
                                    <li key={`framework-${type}-${idx}-${String(framework).substring(0, 30)}`}>{String(framework).trim()}</li>
                                  ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Panel specific fields */}
                      {type === 'panel' && typedPrep.panel_structure && (
                        <div className="prep-item">
                          <strong>Panel Structure:</strong>
                          <p>{typedPrep.panel_structure}</p>
                        </div>
                      )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Personalized Recommendations */}
      {personalized_recommendations && (
        <div className="prep-section personalized-section">
          <h4>✨ Personalized Recommendations</h4>
          
          {personalized_recommendations.strengths_to_highlight && 
           Array.isArray(personalized_recommendations.strengths_to_highlight) && 
           personalized_recommendations.strengths_to_highlight.length > 0 && (
            <div className="info-card strengths-card">
              <h5>💪 Strengths to Highlight</h5>
              <ul>
                {personalized_recommendations.strengths_to_highlight
                  .filter((strength: string | null | undefined) => strength != null && String(strength).trim())
                  .map((strength: string, idx: number) => (
                    <li key={`strength-${idx}-${String(strength).substring(0, 30)}`}>{String(strength).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
          
          {personalized_recommendations.gaps_to_address && 
           Array.isArray(personalized_recommendations.gaps_to_address) && 
           personalized_recommendations.gaps_to_address.length > 0 && (
            <div className="info-card gaps-card">
              <h5>📚 Gaps to Address</h5>
              <ul>
                {personalized_recommendations.gaps_to_address
                  .filter((gap: string | null | undefined) => gap != null && String(gap).trim())
                  .map((gap: string, idx: number) => (
                    <li key={`gap-${idx}-${String(gap).substring(0, 30)}`}>{String(gap).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
          
          {personalized_recommendations.learning_resources && 
           Array.isArray(personalized_recommendations.learning_resources) && 
           personalized_recommendations.learning_resources.length > 0 && (
            <div className="info-card learning-card">
              <h5>📖 Learning Resources</h5>
              <ul>
                {personalized_recommendations.learning_resources
                  .filter((resource: string | null | undefined) => resource != null && String(resource).trim())
                  .map((resource: string, idx: number) => (
                    <li key={`learn-resource-${idx}-${String(resource).substring(0, 30)}`}>{String(resource).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
          
          {personalized_recommendations.practice_suggestions && 
           Array.isArray(personalized_recommendations.practice_suggestions) && 
           personalized_recommendations.practice_suggestions.length > 0 && (
            <div className="info-card practice-card">
              <h5>🎯 Practice Suggestions</h5>
              <ul>
                {personalized_recommendations.practice_suggestions
                  .filter((suggestion: string | null | undefined) => suggestion != null && String(suggestion).trim())
                  .map((suggestion: string, idx: number) => (
                    <li key={`suggestion-${idx}-${String(suggestion).substring(0, 30)}`}>{String(suggestion).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
          
          {personalized_recommendations.questions_to_ask_interviewer && 
           Array.isArray(personalized_recommendations.questions_to_ask_interviewer) && 
           personalized_recommendations.questions_to_ask_interviewer.length > 0 && (
            <div className="info-card questions-card">
              <h5>❓ Questions to Ask Interviewer</h5>
              <ul>
                {personalized_recommendations.questions_to_ask_interviewer
                  .filter((question: string | null | undefined) => question != null && String(question).trim())
                  .map((question: string, idx: number) => (
                    <li key={`question-ask-${idx}-${String(question).substring(0, 30)}`}>{String(question).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
          
          {personalized_recommendations.common_mistakes_to_avoid && 
           Array.isArray(personalized_recommendations.common_mistakes_to_avoid) && 
           personalized_recommendations.common_mistakes_to_avoid.length > 0 && (
            <div className="info-card mistakes-card">
              <h5>⚠️ Common Mistakes to Avoid</h5>
              <ul>
                {personalized_recommendations.common_mistakes_to_avoid
                  .filter((mistake: string | null | undefined) => mistake != null && String(mistake).trim())
                  .map((mistake: string, idx: number) => (
                    <li key={`mistake-${idx}-${String(mistake).substring(0, 30)}`}>{String(mistake).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
          
          {personalized_recommendations.follow_up_strategies && 
           Array.isArray(personalized_recommendations.follow_up_strategies) && 
           personalized_recommendations.follow_up_strategies.length > 0 && (
            <div className="info-card followup-card">
              <h5>📧 Follow-up Strategies</h5>
              <ul>
                {personalized_recommendations.follow_up_strategies
                  .filter((strategy: string | null | undefined) => strategy != null && String(strategy).trim())
                  .map((strategy: string, idx: number) => (
                    <li key={`strategy-${idx}-${String(strategy).substring(0, 30)}`}>{String(strategy).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
          
          {personalized_recommendations.company_specific_talking_points && 
           Array.isArray(personalized_recommendations.company_specific_talking_points) && 
           personalized_recommendations.company_specific_talking_points.length > 0 && (
            <div className="info-card talking-points-card">
              <h5>💬 Company-Specific Talking Points</h5>
              <ul>
                {personalized_recommendations.company_specific_talking_points
                  .filter((point: string | null | undefined) => point != null && String(point).trim())
                  .map((point: string, idx: number) => (
                    <li key={`talking-point-${idx}-${String(point).substring(0, 30)}`}>{String(point).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
          
          {personalized_recommendations.red_flags_to_watch_for && 
           Array.isArray(personalized_recommendations.red_flags_to_watch_for) && 
           personalized_recommendations.red_flags_to_watch_for.length > 0 && (
            <div className="info-card red-flags-card">
              <h5>🚩 Red Flags to Watch For</h5>
              <ul>
                {personalized_recommendations.red_flags_to_watch_for
                  .filter((flag: string | null | undefined) => flag != null && String(flag).trim())
                  .map((flag: string, idx: number) => (
                    <li key={`red-flag-${idx}-${String(flag).substring(0, 30)}`}>{String(flag).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
          
          {personalized_recommendations.confidence_building_tips && 
           Array.isArray(personalized_recommendations.confidence_building_tips) && 
           personalized_recommendations.confidence_building_tips.length > 0 && (
            <div className="info-card confidence-card">
              <h5>💪 Confidence Building Tips</h5>
              <ul>
                {personalized_recommendations.confidence_building_tips
                  .filter((tip: string | null | undefined) => tip != null && String(tip).trim())
                  .map((tip: string, idx: number) => (
                    <li key={`confidence-tip-${idx}-${String(tip).substring(0, 30)}`}>{String(tip).trim()}</li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

